import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Accordion,
  Badge,
  Button,
  Card,
  Col,
  Form,
  Modal,
  Row,
  Table,
} from 'react-bootstrap';
import { AlertTriangle } from 'react-feather';
import { toast } from 'react-toastify';
import { archivePlanAPI, createPlanAPI, getPlanCatalogAPI } from '../../../Services/mockApi';
import PageHeader from '../../../Components/shared/PageHeader';
import LoadingSkeleton from '../../../Components/shared/LoadingSkeleton';
import EmptyState from '../../../Components/shared/EmptyState';
import ConfirmModal from '../../../Components/shared/ConfirmModal';
import { platformUrl } from '../../../utils/mhNav';
import styles from './PlanCatalog.module.scss';

function CellVal({ v }) {
  if (typeof v === 'boolean') return v ? '✓' : '✗';
  return v;
}

export default function PlanCatalog() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const [compare, setCompare] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [archiveTarget, setArchiveTarget] = useState(null);
  const [form, setForm] = useState({
    name: '',
    price: 199,
    userLimit: 50,
    meetingQuota: 500,
    aiTokenQuota: 500000,
    storageGB: 25,
    billingCycle: 'monthly',
  });

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getPlanCatalogAPI();
      setData(res.data);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const visiblePlans = useMemo(() => {
    const p = data?.plans || [];
    return showArchived ? p : p.filter((x) => !x.archived);
  }, [data, showArchived]);

  const matrixDiff = useCallback(
    (row) => {
      if (!compare || !data?.featureMatrix?.length) return false;
      const vals = ['starter', 'growth', 'enterprise'].map((c) => row[c]);
      return new Set(vals.map((v) => JSON.stringify(v))).size > 1;
    },
    [compare, data]
  );

  if (loading && !data) {
    return (
      <div>
        <PageHeader
          title="Plan Catalog"
          breadcrumbs={[
            { label: 'Platform', path: platformUrl('dashboard') },
            { label: 'Subscriptions', path: platformUrl('subscriptions') },
            { label: 'Plans' },
          ]}
        />
        <LoadingSkeleton rows={5} cols={3} />
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <PageHeader
          title="Plan Catalog"
          breadcrumbs={[
            { label: 'Platform', path: platformUrl('dashboard') },
            { label: 'Subscriptions', path: platformUrl('subscriptions') },
            { label: 'Plans' },
          ]}
        />
        <EmptyState
          icon={AlertTriangle}
          title="Could not load plans"
          description={error.message}
          actionLabel="Retry"
          onAction={load}
        />
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Plan Catalog"
        breadcrumbs={[
          { label: 'Platform', path: platformUrl('dashboard') },
          { label: 'Subscriptions', path: platformUrl('subscriptions') },
          { label: 'Plans' },
        ]}
        primaryAction={{ label: 'Create Plan', onClick: () => setCreateOpen(true) }}
        secondaryActions={[
          {
            label: compare ? 'Compare On' : 'Compare Plans',
            variant: compare ? 'primary' : 'outline-secondary',
            onClick: () => setCompare((v) => !v),
          },
        ]}
      />

      <div className="d-flex flex-wrap align-items-center gap-3 mb-3">
        <Form.Check
          type="switch"
          id="arch"
          label="Show Archived"
          checked={showArchived}
          onChange={(e) => setShowArchived(e.target.checked)}
        />
      </div>

      <Row className="g-3 mb-4">
        {visiblePlans.map((plan) => (
          <Col md={6} xl={3} key={plan.id}>
            <Card className={`h-100 ${plan.archived ? styles.archived : ''}`}>
              <Card.Body>
                <h3 className="h5">{plan.name}</h3>
                <div className="fs-4 fw-bold mb-2">
                  {plan.priceLabel || `$${plan.price}`}
                  {!plan.priceLabel && <span className="fs-6 text-muted fw-normal">/mo</span>}
                </div>
                <p className="text-muted small mb-2">Users: {plan.userLimit ?? 'Custom'}</p>
                <ul className="small ps-3">
                  {(plan.features || []).slice(0, 5).map((f) => (
                    <li key={f}>{f}</li>
                  ))}
                </ul>
                <Badge bg="secondary" className="mb-3">
                  {plan.activeTenantsCount ?? 0} active tenants
                </Badge>
                <div className="d-flex flex-wrap gap-1">
                  <Button size="sm" variant="outline-primary" onClick={() => toast.info('Edit (simulated)')}>
                    Edit
                  </Button>
                  <Button size="sm" variant="outline-secondary" onClick={() => toast.success('Cloned (simulated)')}>
                    Clone
                  </Button>
                  <Button size="sm" variant="outline-danger" disabled={plan.archived} onClick={() => setArchiveTarget(plan)}>
                    Archive
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>

      <Card className="mb-4">
        <Card.Header>Feature matrix</Card.Header>
        <Card.Body className="p-0">
          <Table responsive bordered className="mb-0">
            <thead>
              <tr>
                <th>Capability</th>
                <th>Starter</th>
                <th>Growth</th>
                <th>Enterprise</th>
              </tr>
            </thead>
            <tbody>
              {(data.featureMatrix || []).map((row) => (
                <tr key={row.key}>
                  <td>{row.label}</td>
                  {['starter', 'growth', 'enterprise'].map((col) => (
                    <td
                      key={col}
                      className={matrixDiff(row) ? styles.diffCell : ''}
                    >
                      <CellVal v={row[col]} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      <Accordion defaultActiveKey="">
        <Accordion.Item eventKey="0">
          <Accordion.Header>Plan Version History</Accordion.Header>
          <Accordion.Body>
            <Table size="sm" responsive>
              <thead>
                <tr>
                  <th>Version</th>
                  <th>Published by</th>
                  <th>Date</th>
                  <th>Notes</th>
                </tr>
              </thead>
              <tbody>
                {(data.versionHistory || []).map((v) => (
                  <tr key={v.version}>
                    <td>{v.version}</td>
                    <td>{v.publishedBy}</td>
                    <td>{new Date(v.date).toLocaleString()}</td>
                    <td>{v.notes}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Accordion.Body>
        </Accordion.Item>
      </Accordion>

      <Modal show={createOpen} onHide={() => setCreateOpen(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Create plan</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row className="g-2">
            <Col md={6}>
              <Form.Label>Name</Form.Label>
              <Form.Control
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              />
            </Col>
            <Col md={6}>
              <Form.Label>Price / mo</Form.Label>
              <Form.Control
                type="number"
                value={form.price}
                onChange={(e) => setForm((f) => ({ ...f, price: +e.target.value }))}
              />
            </Col>
            <Col md={4}>
              <Form.Label>User limit</Form.Label>
              <Form.Control
                type="number"
                value={form.userLimit}
                onChange={(e) => setForm((f) => ({ ...f, userLimit: +e.target.value }))}
              />
            </Col>
            <Col md={4}>
              <Form.Label>Meetings</Form.Label>
              <Form.Control
                type="number"
                value={form.meetingQuota}
                onChange={(e) => setForm((f) => ({ ...f, meetingQuota: +e.target.value }))}
              />
            </Col>
            <Col md={4}>
              <Form.Label>AI tokens</Form.Label>
              <Form.Control
                type="number"
                value={form.aiTokenQuota}
                onChange={(e) => setForm((f) => ({ ...f, aiTokenQuota: +e.target.value }))}
              />
            </Col>
            <Col md={4}>
              <Form.Label>Storage GB</Form.Label>
              <Form.Control
                type="number"
                value={form.storageGB}
                onChange={(e) => setForm((f) => ({ ...f, storageGB: +e.target.value }))}
              />
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setCreateOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={async () => {
              try {
                await createPlanAPI({ ...form, features: ['New plan feature'] });
                toast.success('Plan created');
                setCreateOpen(false);
                load();
              } catch (e) {
                toast.error(e.message);
              }
            }}
          >
            Save
          </Button>
        </Modal.Footer>
      </Modal>

      <ConfirmModal
        isOpen={Boolean(archiveTarget)}
        onClose={() => setArchiveTarget(null)}
        onConfirm={async () => {
          try {
            await archivePlanAPI(archiveTarget.id);
            toast.success('Plan archived');
            setArchiveTarget(null);
            load();
          } catch (e) {
            toast.error(e.message);
          }
        }}
        title="Archive plan?"
        message={`Archive ${archiveTarget?.name}? Existing tenants keep entitlements until migrated.`}
        confirmLabel="Archive"
        danger
      />
    </div>
  );
}
